import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ChevronUp, Mail, Instagram, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const toggleAccordion = (section: string) => {
    setOpenAccordion(openAccordion === section ? null : section);
  };

  const menuVariants = {
    closed: {
      x: '100%',
      transition: {
        type: 'tween' as const,
        duration: 0.3,
      }
    },
    open: {
      x: 0,
      transition: {
        type: 'tween' as const,
        duration: 0.3,
      }
    }
  };

  const workCategories = [
    {
      id: 'drawings',
      title: '🎨 Drawings',
      icon: '🎨',
      subcategories: [
        { name: 'Watercolor', description: 'Beautiful watercolor paintings' },
        { name: 'Sketches', description: 'Detailed pencil sketches' },
        { name: 'Custom Art', description: 'Personalized artwork' }
      ]
    },
    {
      id: 'candles',
      title: '🕯️ Candle Art',
      icon: '🕯️',
      subcategories: [
        { name: 'Decorated Candles', description: 'Artistically decorated candles' },
        { name: 'Festival Candles', description: 'Special occasion candles' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-cream-50">
      {/* Header Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-sm border-b border-cream-200">
        <div className="flex justify-between items-center px-4 py-4">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-2xl font-serif font-semibold text-warm-800"
          >
            Hand&Heart
          </motion.div>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-cream-200 transition-colors"
          >
            <Menu className="w-6 h-6 text-warm-700" />
          </motion.button>
        </div>
      </header>

      {/* Side Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
              className="fixed inset-0 bg-black/20 z-40"
            />
            <motion.div
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed top-0 right-0 h-full w-80 bg-white shadow-xl z-50"
            >
              <div className="flex justify-between items-center p-6 border-b border-cream-200">
                <h2 className="text-xl font-serif font-semibold text-warm-800">Menu</h2>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-cream-200 transition-colors"
                >
                  <X className="w-6 h-6 text-warm-700" />
                </button>
              </div>
              <nav className="p-6">
                <ul className="space-y-4">
                  <li>
                    <Link
                      to="/"
                      onClick={() => setIsMenuOpen(false)}
                      className="block text-lg text-warm-700 hover:text-warm-900 transition-colors"
                    >
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      onClick={() => setIsMenuOpen(false)}
                      className="block text-lg text-warm-700 hover:text-warm-900 transition-colors"
                    >
                      Contact
                    </Link>
                  </li>
                </ul>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="section-padding pt-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-6"
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-serif font-bold text-warm-800 leading-tight">
              Reshmi
            </h1>
            <p className="text-xl sm:text-2xl text-warm-600 font-medium">
              Visual Artist | Craft Enthusiast
            </p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="w-24 h-1 bg-warm-400 mx-auto rounded-full"
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-12"
          >
            <div className="bg-gradient-to-br from-cream-200 to-cream-300 rounded-2xl p-8 sm:p-12">
              <p className="text-lg sm:text-xl text-warm-700 leading-relaxed">
                Welcome to my artistic journey. I create with passion, bringing beauty and warmth 
                to every piece through drawings and candle art. Each creation is crafted with 
                love and attention to detail.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Work Showcase Section */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-warm-800 mb-4">
              Explore My Work
            </h2>
            <p className="text-lg text-warm-600 max-w-2xl mx-auto">
              Discover my collection of drawings and candle art, each piece telling its own story
            </p>
          </motion.div>

          <div className="space-y-6">
            {workCategories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card"
              >
                <button
                  onClick={() => toggleAccordion(category.id)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <div className="flex items-center space-x-4">
                    <span className="text-2xl">{category.icon}</span>
                    <h3 className="text-xl font-semibold text-warm-800">
                      {category.title}
                    </h3>
                  </div>
                  {openAccordion === category.id ? (
                    <ChevronUp className="w-6 h-6 text-warm-600" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-warm-600" />
                  )}
                </button>

                <AnimatePresence>
                  {openAccordion === category.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-6 space-y-6">
                        {category.subcategories.map((subcategory, subIndex) => (
                          <motion.div
                            key={subcategory.name}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: subIndex * 0.1 }}
                            className="bg-cream-50 rounded-lg p-6"
                          >
                            <h4 className="text-lg font-medium text-warm-800 mb-2">
                              {subcategory.name}
                            </h4>
                            <p className="text-warm-600 mb-4">
                              {subcategory.description}
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                              {[1, 2, 3].map((item) => (
                                <img
                                  key={item}
                                  src="/w1.jpeg"
                                  alt={`${subcategory.name} artwork ${item}`}
                                  className="aspect-square object-cover rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                                />
                              ))}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-cream-100">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-warm-800">
              Get in Touch
            </h2>
            <p className="text-lg text-warm-600 max-w-2xl mx-auto">
              Interested in my work? Let's connect and discuss your artistic vision.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center space-x-2"
              >
                <Mail className="w-5 h-5" />
                <span>Contact Me</span>
              </Link>
              
              <a
                href="https://instagram.com/reshmivaisakh28"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center space-x-2"
              >
                <Instagram className="w-5 h-5" />
                <span>Follow on Instagram</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-warm-800 text-white py-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex items-center justify-center space-x-2 mb-4"
          >
            <Heart className="w-5 h-5 text-red-400" />
            <span className="text-lg font-medium">Hand&Heart</span>
          </motion.div>
          <p className="text-cream-200">
            © 2025 Reshmi. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home; 